import { Logger } from 'src/utils/logger';
import { collectPreservedEntries } from './session-preserve.logic';

/**
 * StorageService
 * 
 * Provides centralized client-side storage management with:
 * - Centralized key registry for all storage keys
 * - Comprehensive cleanup methods for logout
 * - Prefix-based cleanup for dynamic keys
 * - IndexedDB cleanup for file caches
 * 
 * NOTE: This service does NOT provide encryption. True security for tokens
 * requires HttpOnly cookies (backend change). This service manages cleanup.
 */

// Registry of all storage keys used in the application
export const STORAGE_KEYS = {
  // Sensitive - HIGH RISK
  JWT: 'jwt-becreatives',
  LOGGED_IN_USER: 'loggedInUser',
  
  // Medium sensitivity
  VERIFY_EMAIL: 'bc_verify_email',
  MEDIA_UPLOADS: 'media_uploads_persisted',
  
  // Low sensitivity - preferences/UI state
  AGENT_ID: 'be_creatives_agent_id',
  THEME: 'theme',
  DEFAULT_PLAN: 'defaultPlan',
  HIDE_ONBOARDING: 'hideFirstSaveOnboardingModal',
  PRE_LOGIN_LOCATION: 'intendedPreLoginAuthLocation',
  DISMISSED_SESSIONS: 'dismissed_cancelled_sessions',
  FROM_PERSONAL_PLAN_PURCHASE: 'from_personal_plan_purchase',

  // Session-only keys (sessionStorage)
  SESSION: {
    // Per-document ON PURPOSE. The note says "the next script beN saves should UPDATE script X",
    // and it must not outlive the document that put the chip on screen. In localStorage it was
    // shared across every tab and every WebView on the origin — the mobile app keeps a PRELOADED
    // ai-agent WebView — and it survived a crash or force-quit, so a save could land on a script
    // whose chip the user had never seen. sessionStorage dies with the browsing context, which
    // removes that class outright rather than guarding against it.
    EDIT_SCRIPT_CONTEXT: 'edit_script_context',
    SCRIPT_GENERATION_PROGRESS: 'scriptGenerationInProgress',
    SCRIPT_GENERATION_TIME: 'scriptGenerationStartTime',
    GENERATING_SCRIPT_ID: 'generatingScriptId',
    GENERATED_SCRIPT_TITLE: 'generatedScriptTitle',
    GENERATED_SCRIPT_CONTENT: 'generatedScriptContent',
    CONTENT_PLAN: 'dashboard-selected-content-plan',
    DIRECTORY_SELECTION: 'previousDirectorySelectionState',
    DIRECTORY_NAVIGATION: 'directoryNavigationContext',
    PERSISTENT_DIRECTORY: 'persistentDirectorySelections',
    // Loop-bounding counters, not user data — see SESSION_STORAGE_PRESERVED_KEYS
    WV_AUTH_RECOVERY_ATTEMPTS: 'wv_auth_recovery_attempts',
    WV_AUTH_REPLY_NAVIGATIONS: 'wv_auth_reply_navigations',
  },
  
  // Dynamic keys with prefixes
  PREFIXES: {
    CONTENT: 'content_',
    CONTENT_PLAN: 'contentPlan_',
    CANCELLED_MESSAGE: 'cancelled_message_',
    AUTO_EXTEND: 'autoExtendCheck_',
    SAVED_MEDIA: 'saved_to_media_library_',
  },
} as const;

// Keys that contain sensitive data and should be cleared on logout
export const SENSITIVE_KEYS = [
  STORAGE_KEYS.JWT,
  STORAGE_KEYS.LOGGED_IN_USER,
  STORAGE_KEYS.VERIFY_EMAIL,
] as const;

// Keys that should be cleared on logout (all user-specific data)
export const USER_DATA_KEYS = [
  ...SENSITIVE_KEYS,
  STORAGE_KEYS.AGENT_ID,
  STORAGE_KEYS.MEDIA_UPLOADS,
  STORAGE_KEYS.PRE_LOGIN_LOCATION,
] as const;

/**
 * sessionStorage keys that must SURVIVE logout cleanup.
 *
 * These are not user data — they are counters that cap how many times the app may reload
 * itself, so clearing them defeats the cap. Note the path that makes this load-bearing
 * rather than theoretical: refresh() calls endSession() on every load with a stale token,
 * and endSession() reaches clearSessionStorage(). A counter wiped there resets the bound
 * on every reload, which turns bounded recovery into an unbounded loop.
 */
export const SESSION_STORAGE_PRESERVED_KEYS = [
  STORAGE_KEYS.SESSION.WV_AUTH_RECOVERY_ATTEMPTS,
  STORAGE_KEYS.SESSION.WV_AUTH_REPLY_NAVIGATIONS,
] as const;

class StorageServiceClass {
  /**
   * Remove a specific key
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      Logger.error('Failed to remove storage key', 'StorageService', { key, error: e });
    }
  }

  /**
   * Clear all sensitive data (for logout)
   */
  clearSensitiveData(): void {
    Logger.info('Clearing sensitive storage data', 'StorageService');
    
    SENSITIVE_KEYS.forEach((key) => {
      this.remove(key);
    });
  }

  /**
   * Clear all user-specific data (comprehensive logout cleanup)
   */
  clearUserData(): void {
    Logger.info('Clearing all user storage data', 'StorageService');

    // Clear known user data keys
    USER_DATA_KEYS.forEach((key) => {
      this.remove(key);
    });

    // Clear dynamic keys with known prefixes
    this.clearByPrefix(STORAGE_KEYS.PREFIXES.CONTENT);
    this.clearByPrefix(STORAGE_KEYS.PREFIXES.CONTENT_PLAN);
    this.clearByPrefix(STORAGE_KEYS.PREFIXES.CANCELLED_MESSAGE);
    this.clearByPrefix(STORAGE_KEYS.PREFIXES.AUTO_EXTEND);
    this.clearByPrefix(STORAGE_KEYS.PREFIXES.SAVED_MEDIA);
    
    // Clear dismissed cancelled sessions
    this.remove(STORAGE_KEYS.DISMISSED_SESSIONS);

    // The note moved to sessionStorage, but builds already in the wild wrote it to localStorage
    // and nothing reads that copy any more. Swept here rather than at boot deliberately: it is
    // dead weight, not a correctness problem, so it is not worth a startup call site — which does
    // mean a user who never logs out keeps the entry. Removed by literal key, not STORAGE_KEYS,
    // because the constant now points at the sessionStorage one.
    this.remove('edit_script_context');

    // Clear all sessionStorage
    this.clearSessionStorage();
  }

  /**
   * Clear localStorage keys matching a prefix
   * Optimized to O(n) using Object.keys() snapshot
   */
  clearByPrefix(prefix: string): void {
    try {
      // Object.keys() creates a snapshot array, safe to delete during iteration
      const keys = Object.keys(localStorage);
      let removedCount = 0;
      
      for (const key of keys) {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
          removedCount++;
        }
      }

      if (removedCount > 0) {
        Logger.info(`Cleared ${removedCount} keys with prefix: ${prefix}`, 'StorageService');
      }
    } catch (e) {
      Logger.error('Failed to clear keys by prefix', 'StorageService', { prefix, error: e });
    }
  }

  /**
   * Clear all sessionStorage
   */
  clearSessionStorage(): void {
    try {
      // Preserve loop-bounding counters — see SESSION_STORAGE_PRESERVED_KEYS for why
      // wiping them here un-bounds the reload loop they exist to cap.
      const preserved = collectPreservedEntries(
        (key) => sessionStorage.getItem(key),
        SESSION_STORAGE_PRESERVED_KEYS,
      );

      sessionStorage.clear();

      // Restore per KEY, not as one block. These two keys are the only thing bounding the
      // WebView reload loop, so a throw partway through (quota, or storage revoked between
      // the read and the write — the same condition every other try/catch on this path
      // exists for) must not cost the remaining ones. Inside the outer try it silently lost
      // them and reported the failure as "Failed to clear sessionStorage", pointing at the
      // wrong operation: a half-restore re-enables the loop this mechanism exists to cap.
      for (const [key, value] of preserved) {
        try {
          sessionStorage.setItem(key, value);
        } catch (e) {
          Logger.error('Failed to restore a preserved sessionStorage key', 'StorageService', {
            key,
            error: e,
          });
        }
      }

      Logger.info('Cleared sessionStorage', 'StorageService');
    } catch (e) {
      Logger.error('Failed to clear sessionStorage', 'StorageService', e);
    }
  }

  /**
   * Clear IndexedDB (for file cache cleanup)
   */
  async clearIndexedDB(): Promise<void> {
    try {
      const databases = await indexedDB.databases();
      for (const db of databases) {
        if (db.name) {
          indexedDB.deleteDatabase(db.name);
          Logger.info(`Deleted IndexedDB: ${db.name}`, 'StorageService');
        }
      }
    } catch (e) {
      // indexedDB.databases() may not be supported in all browsers
      try {
        indexedDB.deleteDatabase('BeCreativesFileCache');
        Logger.info('Deleted BeCreativesFileCache IndexedDB', 'StorageService');
      } catch (innerError) {
        Logger.error('Failed to clear IndexedDB', 'StorageService', innerError);
      }
    }
  }

  /**
   * Full cleanup - clears everything (for complete logout)
   */
  async fullCleanup(): Promise<void> {
    Logger.info('Performing full storage cleanup', 'StorageService');
    
    this.clearUserData();
    await this.clearIndexedDB();
  }

  /**
   * Audit storage - returns list of all stored keys (for debugging)
   */
  auditStorage(): { localStorage: string[]; sessionStorage: string[] } {
    const localStorageKeys: string[] = [];
    const sessionStorageKeys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) localStorageKeys.push(key);
    }

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) sessionStorageKeys.push(key);
    }

    return { localStorage: localStorageKeys, sessionStorage: sessionStorageKeys };
  }
}

export const StorageService = new StorageServiceClass();
